import type { ReportFreshness } from '@/api/types';
import { dayYmd } from '@/lib/datetime';

export type ReportDayFamily = 'delivery' | 'attribution';

export function calendarDayKeys(dateFrom: string, dateTo: string): string[] {
  const result: string[] = [];
  const cursor = new Date(`${dateFrom}T00:00:00Z`);
  const end = new Date(`${dateTo}T00:00:00Z`);
  while (cursor <= end) {
    result.push(dayYmd(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
}

export function reportingPublishedDays(
  freshness: ReportFreshness | null | undefined,
  family: ReportDayFamily,
): ReadonlySet<string> {
  if (family === 'delivery') {
    return new Set(freshness?.delivery_published_days ?? []);
  }
  return new Set(freshness?.attribution_published_days ?? []);
}

/**
 * Days on which absence of a row is a confirmed zero. A partial delivery
 * publication is deliberately excluded: some expected provider scopes may
 * still be missing even though another scope produced a lower-bound row.
 */
export function reportingZeroFillDays(
  freshness: ReportFreshness | null | undefined,
  family: ReportDayFamily,
): ReadonlySet<string> {
  if (family === 'delivery') {
    return new Set(freshness?.delivery_covered_days ?? []);
  }
  return reportingPublishedDays(freshness, 'attribution');
}

/**
 * Builds a calendar-aligned series without equating absence with zero.
 *
 * A row is retained even when the per-day manifest is absent so non-zero
 * lower-bound facts remain visible during a rolling deployment. A missing row
 * becomes zero only when the publication manifest proves that day was covered.
 */
export function coverageAwareSeries(
  days: readonly string[],
  valuesByDay: ReadonlyMap<string, number>,
  coveredDays: ReadonlySet<string>,
): Array<number | null> {
  return days.map((day) => {
    if (valuesByDay.has(day)) return valuesByDay.get(day)!;
    return coveredDays.has(day) ? 0 : null;
  });
}

export function intersectDayCoverage(
  left: ReadonlySet<string>,
  right: ReadonlySet<string>,
): ReadonlySet<string> {
  return new Set(Array.from(left).filter((day) => right.has(day)));
}

export function maskCalendarSeries(
  values: readonly number[],
  days: readonly string[],
  coveredDays: ReadonlySet<string>,
): Array<number | null> {
  return days.map((day, index) => (
    coveredDays.has(day) && index < values.length ? values[index]! : null
  ));
}

export function hasVisibleSeriesValue(
  values: readonly (number | null)[],
): boolean {
  return values.some((value) => value !== null);
}
